import {
    Attachment,
    Button,
    ButtonGroup,
    bytesToSize,
    classNames,
    dispatchCustomEvent,
    Divider,
    FDate,
    Flexer,
    Grid,
    GridItem,
    Header,
    Heading,
    Icon,
    IconLib,
    Image,
    Input,
    logger,
    Menu,
    MenuItem,
    Popover,
    Portal,
    ScrollView,
    stopEvent,
    Text,
    UploadArea,
    useCustomEvent,
    useDialog,
    useTheme,
    useVisibility,
    View,
    waitForRender,
} from '@fold-dev/core'
import EmojiPicker, { Emoji, EmojiClickData } from 'emoji-picker-react'
import { Fragment, useEffect, useId, useMemo, useRef, useState } from 'react'
import {
    TbArrowLeft,
    TbArrowUp,
    TbArrowUpRight,
    TbCornerUpLeft,
    TbDots,
    TbFileUpload,
    TbGif,
    TbMessage,
    TbMessageCircle,
    TbMoodSmile,
    TbPencil,
    TbTrash,
    TbX,
} from 'react-icons/tb'
import { useParams } from 'react-router'
import { v4 as uuidv4 } from 'uuid'
import { LoadingLine, Tiptap } from '../components'
import { typing } from '../contexts/websocket.context'
import {
    aggregateReactions,
    extractUrlFromHtml,
    getMentionsfromTipTapJSON,
    isEmoticon,
    isRichTextBlank,
    stripS3Url,
} from '../helpers'
import {
    useProjectMessageAttachments,
    useProjectMessageReactions,
    useProjectMessages,
    useThreads,
    useTools,
} from '../hooks/api.hooks'
import { useAuth } from '../hooks/auth.hook'
import { useFile } from '../hooks/file.hook'
import { useAppStore } from '../store/app'
import { PresenceAvatar } from './common/presence-avatar'
import { useSubscription } from '../hooks/websocket.hook'

export const CONSECUTIVE_TIME = 1000 * 60 * 2

export const Typing = (props: any) => {
    const [typing, setTyping] = useState([])
    const { workspaceId, projectId, view } = useParams()
    const user = useAppStore((state) => state.user)

    const handleTypingEvent = ({ detail }) => {
        const { id, name, time } = detail
        if (projectId != detail.projectId) return
        if (user.id == id) return

        const isTyping = detail.typing
        const now = new Date()
        const alreadyTyping = typing.find((t) => t.id == id)

        if (!alreadyTyping && isTyping) {
            setTyping([...typing, { id, name, time }])
        }

        if (!isTyping) {
            setTyping(typing.filter((t) => t.id != id))
        }
    }

    useCustomEvent('typing', handleTypingEvent)

    useEffect(() => {
        const cleanupInterval = setInterval(() => {
            setTyping(
                typing.filter((t) => {
                    const now = Date.now()
                    const then = t.time
                    return now - then <= 2000
                })
            )
        }, 2000)
        return () => clearInterval(cleanupInterval)
    }, [typing])

    return (
        <>
            {!!typing.length && (
                <Text
                    size="sm"
                    className="messages__is-typing">
                    {typing.map((t) => t.name).join(', ')} is typing...
                </Text>
            )}
        </>
    )
}

export const GifDemo = ({ id, onDelete = null }) => {
    const [gif, setGif] = useState(undefined)
    const [error, setError] = useState(0)
    const { tenorGet } = useTools()

    const load = () => tenorGet(id).then(setGif).catch(setError)

    useEffect(() => {
        if (id) load()
    }, [id])

    return (
        <View
            row
            bgToken="surface-strong"
            radius="var(--f-radius)"
            border="1px solid var(--f-color-border)"
            height={250}
            width="fit-content"
            position="relative">
            {!!onDelete && (
                <Button
                    size="xs"
                    variant="danger"
                    position="absolute"
                    zIndex={10}
                    radius={50}
                    width={20}
                    height={20}
                    onClick={onDelete}
                    style={{ top: -10, right: -10 }}>
                    <Icon
                        icon={TbX}
                        size="sm"
                    />
                </Button>
            )}

            {!error && (
                <Image
                    src={gif?.media_formats?.gif?.url}
                    border="1px solid var(--f-color-border)"
                    shadow="none"
                    height={250}
                    m="0"
                    p="0"
                    style={{ minWidth: 250 }}
                    width={gif?.media_formats?.gif?.url ? 'fit-content' : '250'}
                />
            )}

            {!!error && (
                <View
                    column
                    gap="1rem">
                    <Text
                        p="0 5rem"
                        textAlign="center">
                        Problem loading this Gif.
                    </Text>
                    <Button
                        size="sm"
                        onClick={load}>
                        Retry?
                    </Button>
                </View>
            )}
        </View>
    )
}

export const Gif = ({ onSelect }) => {
    const [searchTerm, setSearchTerm] = useState('')
    const { systemTheme } = useTheme()
    const { tenorSearch } = useTools()
    const [gifs, setGifs] = useState([])

    useEffect(() => {
        const timeout = setTimeout(() => tenorSearch({ search: searchTerm || 'team work' }).then(setGifs), 500)
        return () => clearTimeout(timeout)
    }, [searchTerm])

    return (
        <View
            height={440}
            width={400}
            p={10}
            style={{ overflow: 'auto' }}>
            <Input
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type to search"
                value={searchTerm}
            />
            <Grid
                m="10px 0 0 0"
                gap={5}
                columns={4}>
                {gifs.map(({ id, media_formats }) => (
                    <GridItem key={id}>
                        <Image
                            className="f-buttonize"
                            src={media_formats.gif.url}
                            onClick={() => onSelect(id)}
                            style={{ minHeight: 80 }}
                        />
                    </GridItem>
                ))}
            </Grid>
        </View>
    )
}

export const CommentAttachment = ({ attachment, onDelete }) => {
    const { name, type, url, filesize } = attachment
    const { getDownloadUrl } = useFile()
    const [src, setSrc] = useState(undefined)
    const size = useMemo(() => bytesToSize(filesize), [filesize])
    const { setDialog, closeDialog } = useDialog()

    const handleUrlClick = async (e) => {
        stopEvent(e)
        const downloadLink = await getDownloadUrl({ filename: url, secure: true })
        window.open(downloadLink.url)
    }

    const handleDelete = async (e) => {
        stopEvent(e)

        setDialog({
            title: 'Are you sure?',
            description: 'This action cannot be undone.',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={() => {
                            onDelete()
                            closeDialog()
                        }}
                        variant="danger">
                        Delete
                    </Button>
                </View>
            ),
        })
    }

    useEffect(() => {
        if (attachment.type.includes('image')) {
            getDownloadUrl({ filename: url, secure: true })
                .then((downloadLink) => setSrc(downloadLink.url))
                .catch((error) => console.log(error))
        }
    }, [attachment])

    if (src) {
        return (
            <View
                column
                gap="0.5rem"
                alignItems="flex-start">
                <div style={{ height: 200 }}>
                    <Image
                        bgToken="surface"
                        border="1px solid var(--f-color-border)"
                        alt={name}
                        width={200}
                        height={200}
                        src={src}
                        className="f-buttonize-outline"
                        onClick={handleUrlClick}
                    />
                </div>
                <View
                    row
                    gap="1rem"
                    justifyContent="flex-start">
                    <Text
                        size="sm"
                        colorToken="text-weaker">
                        {name} - {size}
                    </Text>
                    <Text
                        className="f-buttonize f-underline reveal-on-hover-target"
                        size="sm"
                        colorToken="text-weaker"
                        fontWeight={700}>
                        Delete
                    </Text>
                </View>
            </View>
        )
    }

    return (
        <Attachment
            as="a"
            heroPoster={src}
            size="sm"
            width={200}
            mime={type}
            filesize={24325}
            label={name}
            onClick={handleUrlClick}
            tools={
                <div
                    className="f-row"
                    style={{ gap: 5 }}>
                    <span
                        className="f-buttonize"
                        onClick={handleDelete}>
                        <Icon
                            icon={TbTrash}
                            style={{ color: 'var(--f-color-base-300)' }}
                        />
                    </span>
                    <Icon
                        icon={TbArrowUpRight}
                        style={{ color: 'var(--f-color-base-300)' }}
                    />
                </div>
            }
        />
    )
}

export const InputAttachment = ({ filename, onDelete }) => {
    return (
        <div className="input-attachment">
            <div className="input-attachment__container f-row">
                <div className="f-text f-ellipsis">{filename}</div>
                <Button
                    subtle
                    size="xs"
                    onClick={onDelete}>
                    <Icon
                        icon={TbTrash}
                        size="sm"
                    />
                </Button>
            </div>
        </div>
    )
}

export const MessageBubble = (props) => {
    const { getUserId } = useAuth()
    const { isSidebar } = props
    const { show, hide, visible } = useVisibility()
    const menu = useVisibility()
    const { workspaceId, projectId, view } = useParams()
    const { og } = useTools()
    const {
        id,
        userId,
        userName,
        userImage,
        content,
        attachments,
        createdAt,
        externalEmail,
        totalReplies,
        gifId,
        replyId,
        replyName,
        replyContent,
        replyGifId,
        replyCreatedAt,
    } = props.message
    const messageReactions = useMemo(() => props.message?.reactions || [], [props.message])
    const onlyEmoticon = useMemo(() => isEmoticon(content), [content])
    const messageReactionsAPI = useProjectMessageReactions()
    const messageAttachmentAPI = useProjectMessageAttachments()
    const reactions: any = useMemo(() => aggregateReactions(messageReactions), [messageReactions])
    const isMe = useMemo(() => userId == getUserId(), [userId])
    const isBlank = useMemo(() => isRichTextBlank(content), [content])
    const [counter, setCounter] = useState(0)
    const dateText = useMemo(() => FDate(new Date(createdAt)).fromNow(), [createdAt, counter])
    const replyCreatedAtPretty = useMemo(() => FDate(new Date(replyCreatedAt)).fromNow(), [replyCreatedAt])
    const replyContentPretty = useMemo(
        () => (isBlank && !!replyGifId ? 'Gif attachment' : isBlank ? 'No content' : replyContent),
        [replyContent, replyGifId, isBlank]
    )
    const [edit, setEdit] = useState(false)
    const [preview, setPreview] = useState<any>(null)
    const actions = useAppStore((state) => state.actions)
    const sidebar = useAppStore((state) => state.sidebar)
    const thread = useAppStore((state) => state.thread)
    const ref = useRef(null)
    const className = classNames({
        'f-message-bubble': true,
        'f-row': true,
        'reveal-on-hover': true,
        'is-emoticon': onlyEmoticon,
        'is-edit': edit,
        'is-consecutive': props.isConsecutive,
    })

    const handleAttachmentDelete = async (attachment) => {
        actions.messageAttachmentRemove({ id: attachment.id })
        messageAttachmentAPI.remove(workspaceId, projectId, id, attachment.id)
    }

    const handleReactionAdd = async (emoji: EmojiClickData) => {
        const reaction = emoji.unified
        const reactionId = uuidv4()
        const userId = getUserId()
        const reactions = [...messageReactions, { id: reactionId, reaction, userId }]

        if (messageReactions.findIndex((r) => r.reaction == reaction && r.userId == userId) != -1) return

        actions.messageUpdate({ id, reactions })
        hide()
        await messageReactionsAPI.create(workspaceId, projectId, id, { id: reactionId, reaction })
    }

    const handleReactionDelete = async (reactions) => {
        const userReaction = reactions.find((r) => r.userId == getUserId())

        if (userReaction) {
            if (userReaction.userId != getUserId()) return
            const reactions = messageReactions.filter((reaction) => reaction.id != userReaction.id)
            actions.messageUpdate({ id, reactions })
            await messageReactionsAPI.remove(workspaceId, projectId, id, userReaction.id)
        } else {
            handleReactionAdd(userReaction.reaction)
        }
    }

    const handleThreadOpen = (e) => {
        if (thread.id == id && sidebar == 'thread') return
        // might not have a threadId yet
        actions.setThread({ id, messages: [] })
        setTimeout(() => actions.setSidebar('thread'))
    }

    const fetchOpenGraph = async () => {
        const url = extractUrlFromHtml(content)
        if (url) {
            try {
                const r = await og({ url })
                if (!r.error) {
                    if (!!r.data && !!r.data.result) setPreview({ ...r.data.result, url })
                    if (!!r.result) setPreview({ ...r.result, url })
                } else {
                    setPreview(null)
                }
            } catch (e) {
                setPreview(null)
                console.error(e)
            }
        } else {
            setPreview(null)
        }
    }

    useEffect(() => {
        fetchOpenGraph()
        const interval = setInterval(() => setCounter(new Date().getTime()), 60000)
        return () => clearInterval(interval)
    }, [content])

    return (
        <div className={className}>
            <div className="f-message-bubble__avatar">
                <PresenceAvatar
                    userId={userId}
                    src={userImage}
                    name={userName || externalEmail} // this is the only case where it would be null
                    size="sm"
                    style={{
                        // 'outline': isMe ? '2px solid var(--f-color-accent)' : undefined,
                        // 'outlineOffset': 1,
                        '--f-avatar-background-color': 'var(--f-color-surface-strong)',
                    }}
                />
            </div>

            <div className="f-message-bubble__content f-col">
                <div className="f-message-bubble__header f-row">
                    <span className="f-message-bubble__header-name f-text sm">
                        {isMe ? 'You' : userName || externalEmail}
                    </span>

                    <span className="f-message-bubble__header-date f-text sm">{dateText}</span>
                </div>

                {!!replyId && (
                    <div className="f-message-bubble__reply">
                        <div className="f-message-bubble__reply-name f-text sm">
                            <strong>{replyName}</strong> {replyCreatedAtPretty}
                        </div>
                        <div
                            className="f-message-bubble__reply-content f-text sm"
                            dangerouslySetInnerHTML={{ __html: replyContentPretty }}
                        />
                    </div>
                )}

                {!edit && (
                    <div className="f-message-bubble__main f-row">
                        {!isBlank && (
                            <div
                                className="f-message-bubble__bubble f-col f-text"
                                dangerouslySetInnerHTML={{ __html: content }}
                            />
                        )}

                        <div className="f-message-bubble__toolbar f-row">
                            <ButtonGroup className={menu.visible || visible ? undefined : 'reveal-on-hover-target'}>
                                {!isSidebar && (
                                    <Button
                                        size="xs"
                                        onClick={() => {
                                            props.onThread()
                                            menu.hide()
                                        }}>
                                        <Icon
                                            icon={TbMessageCircle}
                                            size="sm"
                                        />
                                    </Button>
                                )}

                                <Button
                                    size="xs"
                                    onClick={() => {
                                        props.onReply()
                                        menu.hide()
                                    }}>
                                    <Icon
                                        icon={TbCornerUpLeft}
                                        size="sm"
                                    />
                                </Button>

                                <Button
                                    size="xs"
                                    disabled={!isMe}
                                    onClick={() => {
                                        setEdit(true)
                                        props.onEditStart()
                                        setTimeout(() => ref.current.firstChild?.focus())
                                    }}>
                                    <Icon
                                        icon={TbPencil}
                                        size="sm"
                                    />
                                </Button>

                                <Popover
                                    border="none"
                                    width="fit-content"
                                    portal={Portal}
                                    content={
                                        <div className="f-message-bubble__reactions f-row">
                                            <EmojiPicker onEmojiClick={handleReactionAdd} />
                                        </div>
                                    }
                                    isVisible={visible}
                                    onDismiss={hide}>
                                    <Button
                                        size="xs"
                                        onClick={show}>
                                        <Icon
                                            icon={TbMoodSmile}
                                            size="sm"
                                        />
                                    </Button>
                                </Popover>

                                <Popover
                                    width="fit-content"
                                    portal={Portal}
                                    content={
                                        <Menu width={200}>
                                            <MenuItem
                                                onClick={props.onDelete}
                                                prefix={<Icon icon={TbTrash} />}>
                                                Delete
                                            </MenuItem>
                                        </Menu>
                                    }
                                    isVisible={menu.visible}
                                    onDismiss={menu.hide}>
                                    <Button
                                        size="xs"
                                        onClick={menu.show}>
                                        <Icon
                                            icon={TbDots}
                                            size="sm"
                                        />
                                    </Button>
                                </Popover>
                            </ButtonGroup>
                        </div>
                    </div>
                )}

                {edit && (
                    <div className="f-message-bubble__bubble-edit">
                        <Tiptap
                            ref={ref}
                            hideToolbar
                            content={content}
                            onSubmit={props.onEdit}
                            workspaceId={props.workspaceId}
                            projectId={props.projectId}
                            teamId={props.teamId}
                            instanceId={id}
                        />

                        <View
                            row
                            m="0.25rem 0 0 0">
                            <Button
                                size="sm"
                                m="0 auto 0 0"
                                onClick={() => setEdit(false)}>
                                Cancel
                            </Button>
                            <Button
                                size="xs"
                                style={{
                                    '--f-button-background-color': 'var(--f-color-surface-strong)',
                                    '--f-button-background-color-hover': 'var(--f-color-surface-stronger)',
                                }}
                                shadow="none"
                                border="none"
                                radius={50}
                                width={35}
                                height={35}
                                p={0}
                                onClick={() => {
                                    dispatchCustomEvent('tiptap:submit', { instanceId: id })
                                    setEdit(false)
                                }}>
                                <Icon
                                    icon={TbArrowUp}
                                    color="var(--f-color-accent)"
                                />
                            </Button>
                        </View>
                    </div>
                )}

                {!!attachments && !!attachments.length && (
                    <div className="f-message-bubble__attachments f-row">
                        {attachments.map((attachment, index) => (
                            <CommentAttachment
                                key={index}
                                attachment={attachment}
                                onDelete={() => handleAttachmentDelete(attachment)}
                            />
                        ))}
                    </div>
                )}

                {!!gifId && <GifDemo id={gifId} />}

                {!!preview && (
                    <a
                        target="_blank"
                        href={preview.url}
                        className="f-message-bubble__preview f-buttonize-outline">
                        {!!preview.ogImage && !!preview.ogImage[0] && (
                            <Image
                                height={120}
                                src={preview.ogImage[0].url}
                                errorContent="Image not found"
                            />
                        )}
                        {!!preview.ogTitle && <h5 className="f-heading">{preview.ogTitle}</h5>}
                        {!!preview.ogDescription && <span className="f-text">{preview.ogDescription}</span>}
                    </a>
                )}

                {(!!Object.keys(reactions).length || +totalReplies > 0) && (
                    <div className="f-message-bubble__user-reactions f-row">
                        {Object.keys(reactions).map((reaction, index) => (
                            <span
                                key={reaction}
                                onClick={() => handleReactionDelete(reactions[reaction].reactions)}
                                className="f-message-bubble__user-reaction f-buttonize f-row">
                                <Emoji
                                    unified={reaction}
                                    size={18}
                                />
                                &nbsp;
                                {reactions[reaction].count}
                            </span>
                        ))}

                        {+totalReplies > 0 && (
                            <Button
                                p="0 0.5rem"
                                height="2rem"
                                size="sm"
                                fontWeight={600}
                                onClick={handleThreadOpen}>
                                See {totalReplies} {+totalReplies == 1 ? 'reply' : 'replies'}
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export const Reply = ({ message, onCancel }) => {
    return (
        <View
            row
            radius="var(--f-radius)"
            m="0 0 0.25rem 0"
            gap="0.5rem"
            p="1rem"
            width="100%"
            className="f-message-reply">
            <View
                flex={1}
                alignItems="flex-start"
                column
                gap="0.25rem">
                <Text fontWeight={700}>Replying to {message.userName}:</Text>
                <div
                    className="f-text"
                    dangerouslySetInnerHTML={{ __html: message.content }}
                />
            </View>
            <Button
                subtle
                onClick={onCancel}
                size="xs">
                <Icon icon={TbX} />
            </Button>
        </View>
    )
}

export const Messages = ({
    teamId,
    workspaceId,
    projectId,
    isSidebar = false,
    isThreadView = false,
    hideBackArrow = false,
}) => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const { show, hide, visible } = useVisibility()
    const gif = useVisibility()
    const projectMessagesAPI = useProjectMessages()
    const [files, setFiles] = useState([])
    const [internal, setInternal] = useState(false)
    const fileRef = useRef<any>(null)
    const { setDialog, closeDialog } = useDialog()
    const { upload } = useFile()
    const [page, setPage] = useState(0)
    const [gifId, setGifId] = useState(null)
    const [html, setHtml] = useState('')
    const [json, setJson] = useState<any>({})
    const messageReactionsAPI = useProjectMessageReactions()
    const currentThreadId = useRef(null)
    const actions = useAppStore((state) => state.actions)
    const user = useAppStore((state) => state.user)
    const thread = useAppStore((state) => state.thread)
    const project = useAppStore((state) => state.project)
    const messages = useAppStore((state) => state.messages)
    const sidebar = useAppStore((state) => state.sidebar)
    const typingTimeout = useRef(null)
    const [edit, setEdit] = useState(null)
    const noMore = useRef(false)
    const busyFetching = useRef(false)
    const { removeAll } = useFile()
    const { getUserId } = useAuth()
    const [reply, setReply] = useState(null)
    const threadsAPI = useThreads()
    const messageList = useMemo(
        () => (isSidebar && !!thread.id ? thread.messages || [] : messages),
        [isSidebar, messages, thread]
    )
    const instanceId = useId()
    const className = classNames({
        'messages-container': true,
        'f-col': true,
        'is-channel': project.isChannel && !isSidebar,
        'is-sidebar': isSidebar,
    })

    const handleTyping = (e) => {
        if (!projectId) return

        typing(projectId, {
            id: user.id,
            name: user.name.split(' ')[0],
            typing: true,
            projectId,
            time: new Date().getTime(),
        })

        clearTimeout(typingTimeout.current)

        typingTimeout.current = setTimeout(() => {
            typing(projectId, {
                id: user.id,
                name: user.name,
                typing: false,
                projectId,
                time: new Date().getTime(),
            })
        }, 2000)
    }

    const handleFileUpload = async (rawFiles) => {
        setLoading(true)
        setError(null)

        try {
            const fileArray: any = Array.from(rawFiles)

            const fileUploads: any = await Promise.all(
                fileArray.map((file: any) => {
                    return upload({
                        folder: workspaceId,
                        file,
                        secure: true,
                    })
                })
            )
            const files = fileUploads.map(({ url }, index) => {
                return {
                    name: fileArray[index].name,
                    size: fileArray[index].size,
                    url,
                    type: fileArray[index].type,
                }
            })

            setFiles(files)
            setLoading(false)
        } catch (e) {
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleFileChange = async (e) => {
        if (e.target.files.length != 0) handleFileUpload(e.target.files)
    }

    const handleEdit = async (content, json = { content: [] }, editor, id) => {
        if (isRichTextBlank(content)) return

        // TODO: support mentions
        const mentions = getMentionsfromTipTapJSON(json)

        editor.commands.clearContent()
        editor.commands.focus()

        actions.messageUpdate({ id, content })
        setEdit(false)
        await projectMessagesAPI.update(workspaceId, projectId, id, { content })
    }

    const handleThread = (message) => {
        if (thread.id == message.id && sidebar == 'thread') return
        actions.setSidebar('thread')
        actions.setThread({
            id: message.id,
            messages: [],
        })
    }

    const handleReply = (message) => {
        setReply(message)
    }

    const handleTopScroll = async () => {
        if (noMore.current) return
        if (busyFetching.current) return

        busyFetching.current = true

        // setLoading(true)
        setError(null)

        try {
            const newPage = page + 1
            const newMessages = await projectMessagesAPI.getAll(workspaceId, projectId, newPage)

            if (!newMessages.length) {
                noMore.current = true
            } else {
                actions.setMessages([...newMessages, ...messages])
                setPage(newPage)
            }
            // setLoading(false)
            busyFetching.current = false
        } catch (e) {
            // setLoading(false)
            setError(e.message)
            busyFetching.current = false
        }
    }

    const handleFileClick = (e) => (fileRef.current ? fileRef.current.click() : null)

    const handleAddMessage = async (content, json = { content: [] }, editor) => {
        try {
            const isBlank = isRichTextBlank(content)

            if (isBlank && files.length == 0 && !gifId) return

            const isThreadMessage = thread?.id && isSidebar
            const parentId = isThreadMessage ? thread.id : null
            const replyId = reply?.id || null
            const messageId = uuidv4()
            const mentions = getMentionsfromTipTapJSON(json)

            if (parentId) actions.messageReplyCountIncrease(parentId)

            const attachments = files.map((file) => ({
                id: uuidv4(),
                name: file.name,
                filesize: file.size,
                url: stripS3Url(file.url),
                type: file.type,
            }))

            editor.commands.clearContent()
            editor.commands.focus()

            // optimistic!!!
            actions.messageAdd({
                id: messageId,
                content,
                attachments,
                userId: user.id,
                userName: user.name,
                userImage: user.image,
                parentId,
                gifId,
                createdAt: new Date().toISOString(),
                isThreadMessage,
                // this is what the API returns:
                replyId: reply?.id,
                replyGifId: reply?.gifId,
                replyName: reply?.userName,
                replyContent: reply?.content,
                replyCreatedAt: reply?.createdAt,
            })

            setFiles([])
            setReply(null)
            setGifId(undefined)

            scrollDown()

            await projectMessagesAPI.create(workspaceId, projectId, {
                id: messageId,
                content,
                attachments,
                mentions,
                gifId,
                parentId,
                replyId,
            })

            // IF this is a reply in a thread
            // AND IF there is no existing thread Id
            // THEN create a new thread
            // and update it's id
            if (parentId && !thread.threadId) {
                const threads = await threadsAPI.get(workspaceId, projectId, parentId)
                const threadId = !!threads.length
                    ? threads.find((thread) => thread.projectMessageId == parentId).id
                    : null

                if (threadId) {
                    actions.setThreadId(threadId)
                } else {
                    const { id } = await threadsAPI.create(workspaceId, projectId, parentId)
                    actions.setThreadId(id)
                }
            }
        } catch (e) {
            logger(e)
            setError('There has been an error.')
        }
    }

    const handleDelete = async (message) => {
        setDialog({
            title: 'Are you sure?',
            description: 'This action cannot be undone.',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={() => {
                            actions.messageDelete(message.id)
                            actions.messageReplyCountDecrease(thread.id)
                            closeDialog()
                            projectMessagesAPI.remove(workspaceId, projectId, message.id)
                        }}
                        variant="danger">
                        Delete
                    </Button>
                </View>
            ),
        })
    }

    const handleClose = () => {
        actions.setSidebar(null)
        actions.setThread({ id: null, threadId: null, messages: [] })
    }

    const handleThreadBack = () => {
        actions.setThread({ id: null, threadId: null, messages: [] })
    }

    const scrollDown = () => {
        waitForRender(() => dispatchCustomEvent('scrollview:scroll-down', { instanceId }))
    }

    const fetchThreadData = async () => {
        setLoading(true)
        setError(null)

        try {
            const messageId = thread.id
            const threads = await threadsAPI.get(workspaceId, projectId, messageId)
            const threadId = !!threads.length ? threads.find((thread) => thread.projectMessageId == messageId).id : null

            const messages = await projectMessagesAPI.getThread(workspaceId, projectId, messageId)

            actions.setThread({ ...thread, threadId, messages })

            setLoading(false)
            scrollDown()
        } catch (e) {
            setLoading(false)
            setError(e.message)
        }
    }

    const fetchProjectData = async () => {
        setLoading(true)
        setError(null)

        try {
            const messages = await projectMessagesAPI.getAll(workspaceId, projectId, page)
            actions.setMessages(messages)

            setLoading(false)
            scrollDown()
        } catch (e) {
            setLoading(false)
            setError(e.message)
        }
    }

    useSubscription(isThreadView ? projectId : null)

    useCustomEvent('start-early-load-for-channel', () => {
        setLoading(true)
    })

    useCustomEvent('reload', () => {
        if (projectId && workspaceId) fetchProjectData()
        if (projectId && workspaceId && isSidebar && !!thread.id) fetchThreadData()
    })

    useEffect(() => {
        if (projectId && workspaceId && isSidebar && !!thread.id) {
            if (currentThreadId.current != thread.id) fetchThreadData()
            currentThreadId.current = thread.id
        }
    }, [projectId, workspaceId, isSidebar, thread])

    useEffect(() => {
        if (projectId && workspaceId) fetchProjectData()
    }, [projectId, workspaceId])

    return (
        <UploadArea
            height="100%"
            width="100%"
            onDrop={handleFileUpload}>
            {(over) => (
                <div className={className}>
                    {over && (
                        <div className="messages-droparea f-fadein f-col">
                            <Icon
                                icon={TbFileUpload}
                                color="var(--f-color-text-weak)"
                                style={{
                                    '--f-icon-sizing-md': '7rem',
                                    '--f-icon-stroke-width-md': '1',
                                }}
                            />

                            <Heading
                                as="h4"
                                fontWeight={600}>
                                Drop files here to upload
                            </Heading>
                        </div>
                    )}

                    {isSidebar && (
                        <Header
                            bgToken="sidebar"
                            p="0 1rem"
                            height="var(---header-sidebar-height)"
                            className="sidebar-header">
                            <View
                                width="100%"
                                position="relative"
                                row>
                                {!!thread.id && !project.isChannel && !hideBackArrow && (
                                    <Button
                                        size="xs"
                                        m="0 1rem 0 0"
                                        bgToken="surface-strong"
                                        onClick={handleThreadBack}>
                                        <Icon icon={TbArrowLeft} />
                                    </Button>
                                )}
                                <Heading
                                    as="h5"
                                    width="100%"
                                    style={{ textTransform: 'capitalize' }}>
                                    {!!thread.id ? 'Thread' : 'Messages'}
                                </Heading>
                                <Button
                                    size="xs"
                                    bgToken="surface-strong"
                                    onClick={handleClose}>
                                    <Icon
                                        icon={TbX}
                                        size="sm"
                                    />
                                </Button>
                            </View>
                        </Header>
                    )}

                    <div className="messages">
                        <LoadingLine
                            loading={loading}
                            variant="highlight"
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                zIndex: 100,
                            }}
                        />

                        <ScrollView
                            instanceId={instanceId}
                            freeze={edit}
                            stickToBottom
                            className="f-overflow-y-auto f-scrollbar messages-scroll"
                            onScrollToTop={handleTopScroll}>
                            <div className="messages-inner">
                                {messageList.map((message, index) => {
                                    const previousMessage = index == 0 ? null : messageList[index - 1]
                                    const then = !previousMessage
                                        ? new Date('3 December 1999')
                                        : new Date(previousMessage.createdAt)
                                    const now = new Date(message.createdAt)
                                    const isConsecutive =
                                        now.getTime() - then.getTime() < CONSECUTIVE_TIME &&
                                        previousMessage.userId == message.userId &&
                                        !message.replyName
                                    const isDifferentDay = now.getDate() != then.getDate()

                                    return (
                                        <Fragment key={message.id}>
                                            {isDifferentDay && (
                                                <Divider
                                                    m="2rem 0"
                                                    content={
                                                        <div className="date-badge f-text sm">
                                                            {now.toLocaleDateString('en-US', {
                                                                month: 'short',
                                                                day: 'numeric',
                                                            })}
                                                        </div>
                                                    }
                                                    contentPosition="50%"
                                                />
                                            )}

                                            <MessageBubble
                                                isSidebar={isSidebar}
                                                isConsecutive={isConsecutive}
                                                externalEmail={undefined}
                                                message={message}
                                                onDelete={() => handleDelete(message)}
                                                onThread={() => handleThread(message)}
                                                onReply={() => handleReply(message)}
                                                onEdit={(c, j, e) => handleEdit(c, j, e, message.id)}
                                                onEditStart={() => setEdit(true)}
                                                workspaceId={workspaceId}
                                                projectId={projectId}
                                                teamId={teamId}
                                            />
                                        </Fragment>
                                    )
                                })}

                                {!messages.length && !loading && (
                                    <View
                                        column
                                        position="absolute"
                                        style={{ inset: 0 }}
                                        gap="1rem"
                                        p="1rem">
                                        <Icon
                                            icon={TbMessageCircle}
                                            color="var(--f-color-surface-strongest)"
                                            style={{
                                                '--f-icon-sizing-md': '7rem',
                                                '--f-icon-stroke-width-md': '0.75',
                                            }}
                                        />
                                        <Heading
                                            as="h4"
                                            textAlign="center">
                                            No messages yet.
                                        </Heading>
                                        <Text textAlign="center">
                                            Be the first to send a message to this project.
                                        </Text>
                                    </View>
                                )}
                            </div>
                        </ScrollView>

                        <Typing />
                    </div>

                    <div className="messages__footer-container f-col">
                        {!!reply && (
                            <Reply
                                message={reply}
                                onCancel={() => setReply(null)}
                            />
                        )}

                        <div className="messages__footer f-row">
                            <div className="messages__footer-input">
                                <Tiptap
                                    hideToolbar
                                    content=""
                                    onSubmit={handleAddMessage}
                                    workspaceId={workspaceId}
                                    projectId={projectId}
                                    teamId={teamId}
                                    instanceId="messages"
                                    onKeyDown={handleTyping}
                                />

                                {files.map(({ name }, index) => (
                                    <InputAttachment
                                        key={index}
                                        filename={name}
                                        onDelete={() => setFiles(files.filter((f, i) => i != index))}
                                    />
                                ))}

                                {!!gifId && (
                                    <View p="0.5rem 1rem">
                                        <GifDemo
                                            id={gifId}
                                            onDelete={() => setGifId(null)}
                                        />
                                    </View>
                                )}

                                <div className="messages__footer-toolbar f-align-h-middle-left">
                                    <input
                                        multiple
                                        type="file"
                                        className="hide"
                                        ref={fileRef}
                                        onChange={handleFileChange}
                                    />

                                    <Button
                                        subtle
                                        size="xs"
                                        onClick={handleFileClick}>
                                        <IconLib icon="paperclip" />
                                    </Button>

                                    <Button
                                        subtle
                                        size="xs"
                                        onClick={() =>
                                            dispatchCustomEvent('tiptap:mention', { instanceId: 'messages' })
                                        }>
                                        <IconLib icon="at" />
                                    </Button>

                                    <Popover
                                        border="none"
                                        width="fit-content"
                                        portal={Portal}
                                        content={
                                            <EmojiPicker
                                                onEmojiClick={({ emoji }) => {
                                                    dispatchCustomEvent('tiptap:emoji', {
                                                        instanceId: 'messages',
                                                        emoji,
                                                    })
                                                    hide()
                                                }}
                                            />
                                        }
                                        isVisible={visible}
                                        onDismiss={hide}>
                                        <Button
                                            subtle
                                            size="xs"
                                            onClick={show}
                                            className={visible ? undefined : 'reveal-on-hover-target'}>
                                            <Icon icon={TbMoodSmile} />
                                        </Button>
                                    </Popover>

                                    <Popover
                                        border="none"
                                        width="fit-content"
                                        portal={Portal}
                                        content={
                                            <Gif
                                                onSelect={(id) => {
                                                    setGifId(id)
                                                    gif.hide()
                                                }}
                                            />
                                        }
                                        isVisible={gif.visible}
                                        onDismiss={gif.hide}>
                                        <Button
                                            subtle
                                            size="xs"
                                            onClick={gif.show}
                                            className={visible ? undefined : 'reveal-on-hover-target'}>
                                            <Icon icon={TbGif} />
                                        </Button>
                                    </Popover>

                                    <Flexer />

                                    <Button
                                        size="xs"
                                        style={{
                                            '--f-button-background-color': 'var(--f-color-surface-strong)',
                                            '--f-button-background-color-hover': 'var(--f-color-surface-stronger)',
                                        }}
                                        shadow="none"
                                        border="none"
                                        radius={50}
                                        width={35}
                                        height={35}
                                        p={0}
                                        onClick={() =>
                                            dispatchCustomEvent('tiptap:submit', { instanceId: 'messages' })
                                        }>
                                        <Icon icon={TbArrowUp} />
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <View
                            row
                            colorToken="text-weaker"
                            justifyContent="flex-end"
                            width={30}
                            position="absolute"
                            className="f-buttonize"
                            zIndex={1}
                            style={{ bottom: 30, right: 30 }}
                        />
                    </div>
                </div>
            )}
        </UploadArea>
    )
}
